/**
 * iPadOS viewport workarounds.
 *
 * Symptom: the page box ends up the full device height instead of the area
 * actually visible below the browser chrome. Everything sized off the html
 * height - body > #app > .base > .selection > .materials - inherits it, which
 * leaves the page scrollable and pushes the footer below the fold. Rotating
 * the device and rotating back fixes it, because that forces iOS to recompute
 * the viewport.
 *
 * It is the VIEWPORT itself that is stale, not just the numbers we read off
 * it, which is why `100dvh` gets it wrong too and why no measurement-based fix
 * works on its own.
 *
 * WHEN it happens: only when the tool is opened from the dashboard via
 * `window.open(url, '_blank')`. Navigating to the same URL directly is fine.
 * A tab opened that way does its first layout while it is not yet the
 * foreground tab, so iOS hands it viewport metrics that belong to the opener
 * and never revises them. That is why the correction below is driven by
 * visibility and focus as much as by load.
 *
 * Rather than identifying the device up front - iPadOS lies about
 * navigator.platform, and the answer differs between Safari, Chrome for iOS
 * and remote-testing rigs - this module watches for the symptom and corrects
 * it whenever it appears: the html box being a different height from the
 * window. On a healthy viewport those agree and nothing here runs.
 */
import {isIPadDevice} from "./helpers";

const MISMATCH_TOLERANCE = 4; // px - ignore rounding and scrollbar noise
// The meta rewrite does not move the ICB on every engine, so try it a
// couple of times and then let forceViewportHeight() do the work instead.
const MAX_RECOMPUTES = 2;
const POLL_INTERVAL = 250;
const POLL_DURATION = 6000; // measured from when the tab first becomes visible
const SETTLE_DELAYS = [50, 150, 300, 600, 1000, 2000];
const VISIBLE_DELAYS = [0, 100, 400, 900];
// How long to let the meta-tag correction try before falling back to a reload
const RELOAD_AFTER = 1200;
const RELOAD_FLAG = "ipadViewportReloaded";

/** True when this tab was created by window.open from another page. */
const wasOpenedByAnotherTab = () => {
  try {
    return !!window.opener
  } catch (e) {
    return true // a cross-origin opener still means we were opened by one
  }
};

// scss/main.scss uses `height: 100dvh` where available and falls back to
// `var(--app-height, 100%)`, so the variable is only worth maintaining on
// engines without dvh units (iOS below 15.4).
const supportsDvh = !!(window.CSS && window.CSS.supports && window.CSS.supports("height", "100dvh"));

let appHeight = 0;
let recomputeCount = 0;
let originalViewportMeta = null;
let timerIds = [];
let pollId = 0;
let pollStartedAt = 0;

const defer = (fn, delay) => {
  timerIds.push(setTimeout(fn, delay));
};

/** How far the html box is from the window. 0 when the viewport is healthy. */
export const getViewportMismatch = () => document.documentElement.clientHeight - window.innerHeight;

export const viewportIsStale = () => Math.abs(getViewportMismatch()) > MISMATCH_TOLERANCE;

/**
 * Publish the current viewport height as --app-height. Exported so the app's
 * own resize handling can keep it in step; a no-op where dvh units work.
 */
export const syncAppHeight = () => {
  if (supportsDvh) {
    return
  }
  const height = window.innerHeight;
  if (height === appHeight) {
    return // also stops the resize below from feeding back into this
  }
  appHeight = height;
  document.documentElement.style.setProperty("--app-height", `${height}px`);
  // Components that measure themselves in JS (the virtualized product grid,
  // the canvas) only re-measure on a resize, and iOS never fired one.
  window.dispatchEvent(new Event("resize"));
};

/**
 * Force iOS to recompute the viewport - the same recalculation an orientation
 * change triggers, without rotating.
 *
 * Mutating the existing meta tag is not always enough, so the node is replaced
 * outright: iOS re-parses viewport metadata when the element is inserted.
 * `height=device-height` is dropped along the way - that directive pins the
 * layout viewport to the full device height instead of the visible area.
 */
export const recomputeViewport = () => {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta || !meta.parentNode || recomputeCount >= MAX_RECOMPUTES) {
    return
  }
  recomputeCount += 1;

  // Always rebuild from the ORIGINAL content: reading back what we last wrote
  // accumulates the throwaway `initial-scale=1.0001` on every pass.
  if (originalViewportMeta === null) {
    originalViewportMeta = meta.getAttribute("content") || "";
  }
  const content = originalViewportMeta
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part && !/^height\s*=/i.test(part))
    .join(",");

  const parent = meta.parentNode;
  parent.removeChild(meta);

  const replacement = document.createElement("meta");
  replacement.setAttribute("name", "viewport");
  // A momentarily different scale guarantees the re-insert is seen as a change
  replacement.setAttribute("content", `${content},initial-scale=1.0001`);
  parent.appendChild(replacement);

  window.requestAnimationFrame(() => {
    replacement.setAttribute("content", content);
    window.scrollTo(0, 0);
    syncAppHeight();
    // let the JS-measured components (virtualized grid, canvas) re-measure
    window.dispatchEvent(new Event("resize"));
  });
};

/** Kept as the previous name so existing callers keep working. */
export const nudgeViewport = recomputeViewport;

/**
 * Pin the height from window.innerHeight and stop the document scrolling.
 *
 * The layout viewport (the initial containing block) is 980 here while the
 * visible area is 905, and nothing done from inside the page changes that.
 * Note documentElement.clientHeight reports the VIEWPORT, not the html box -
 * which is why it stays 980 even with an inline height on html, and why the
 * "mismatch" below never reaches 0. The document can therefore be scrolled by
 * that 75px however short its content is. We cannot resize the ICB, so we
 * take the document out of the scrolling business instead: overflow hidden on
 * the root, and the body pinned to the visible area with position: fixed.
 */
export const forceViewportHeight = () => {
  const height = window.innerHeight;
  const root = document.documentElement;
  root.style.setProperty("--app-height", `${height}px`);
  root.style.height = `${height}px`;
  root.style.overflow = "hidden";

  const body = document.body;
  if (body) {
    body.style.height = `${height}px`;
    body.style.maxHeight = `${height}px`;
    body.style.overflow = "hidden";
    // the piece that removes the leftover scroll space
    body.style.position = "fixed";
    body.style.top = "0";
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  }

  const app = document.getElementById("app");
  if (app) {
    app.style.height = `${height}px`;
  }

  window.scrollTo(0, 0);
  window.dispatchEvent(new Event("resize"));
  return height;
};

/** Everything worth knowing about the current viewport, for diagnostics. */
export const getViewportState = (reason) => {
  const meta = document.querySelector('meta[name="viewport"]');
  return {
    reason,
    mismatch: getViewportMismatch(),
    innerHeight: window.innerHeight,
    docClientHeight: document.documentElement.clientHeight,
    bodyScrollHeight: document.body ? document.body.scrollHeight : null,
    visualViewportHeight: window.visualViewport ? Math.round(window.visualViewport.height) : null,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    openedByAnotherTab: wasOpenedByAnotherTab(),
    supportsDvh,
    recomputeCount,
    viewportMeta: meta ? meta.getAttribute("content") : null,
  };
};

/**
 * Force the correction now, whether or not the viewport currently looks
 * stale, and log what happened. Meant to be called from wherever the app
 * knows its layout has settled - e.g. the visualizer's finishLoading.
 */
export const fixViewportNow = (reason = "manual") => {
  if (!isIPadDevice()) {
    return
  }
  const before = getViewportState(reason);
  console.log("[ipadFixes] fixViewportNow - before", before);

  recomputeViewport();

  VISIBLE_DELAYS.forEach((delay) => defer(() => {
    const now = getViewportState(`${reason}+${delay}ms`);
    if (Math.abs(now.mismatch) > MISMATCH_TOLERANCE) {
      // the meta-tag recompute did not take - pin the height instead
      const pinned = forceViewportHeight();
      console.log(`[ipadFixes] +${delay}ms still stale (${now.mismatch}px), pinned height to ${pinned}px`, now);
    } else {
      console.log(`[ipadFixes] +${delay}ms viewport ok (mismatch ${now.mismatch}px)`, now);
    }
  }, delay));

  return before;
};

const check = () => {
  syncAppHeight();
  if (viewportIsStale()) {
    recomputeViewport();
  }
};

/**
 * Last resort for a tab opened with window.open.
 *
 * Loading the same URL directly always lays out correctly - it is only the
 * opened tab that inherits a stale viewport - so if the correction above has
 * not taken hold by the time the tab has been visible for a moment, reload it.
 * The tab is in the foreground by then, so the reload behaves like a direct
 * visit. Guarded by a sessionStorage flag so it can happen at most once per
 * tab and can never loop.
 */
const reloadIfStillStale = () => {
  if (!wasOpenedByAnotherTab() || !viewportIsStale()) {
    return
  }
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG)) {
      return
    }
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch (e) {
    return // no storage to guard with - better a wrong height than a loop
  }
  window.location.reload();
};

const startPolling = () => {
  pollStartedAt = Date.now();
  if (pollId) {
    return
  }
  pollId = setInterval(() => {
    if (Date.now() - pollStartedAt > POLL_DURATION || recomputeCount >= MAX_RECOMPUTES) {
      clearInterval(pollId);
      pollId = 0;
      return
    }
    check();
  }, POLL_INTERVAL);
};

// A tab opened with window.open lays out while still in the background, so the
// moment it is actually shown is the moment its viewport needs recomputing.
const handleBecameVisible = () => {
  if (document.visibilityState === "hidden") {
    return
  }
  VISIBLE_DELAYS.forEach((delay) => defer(check, delay));
  startPolling(); // restart the window - this may be the first real layout
  defer(reloadIfStillStale, RELOAD_AFTER);
};

const handleWindowLoad = () => {
  check();
  defer(check, 300);
};

const handleOrientationChange = () => {
  // iOS still reports the previous size during the event itself.
  syncAppHeight();
  defer(check, 0);
  defer(check, 300);
  defer(check, 600);
};

/**
 * Install the workarounds. Safe to call unconditionally - it returns straight
 * away on anything without a touch screen, and on a healthy viewport every
 * check is a no-op.
 */
export const startIPadViewportFixes = () => {
  if (!isIPadDevice()) {
    return
  }
  check();
  window.addEventListener("orientationchange", handleOrientationChange);
  window.addEventListener("load", handleWindowLoad);
  window.addEventListener("pageshow", handleBecameVisible);
  window.addEventListener("focus", handleBecameVisible);
  document.addEventListener("visibilitychange", handleBecameVisible);
  if (window.visualViewport) {
    // unlike window "resize", this does fire when the chrome settles
    window.visualViewport.addEventListener("resize", check);
  }
  SETTLE_DELAYS.forEach((delay) => defer(check, delay));
  startPolling();
  defer(reloadIfStillStale, RELOAD_AFTER);
};

export const stopIPadViewportFixes = () => {
  window.removeEventListener("orientationchange", handleOrientationChange);
  window.removeEventListener("load", handleWindowLoad);
  window.removeEventListener("pageshow", handleBecameVisible);
  window.removeEventListener("focus", handleBecameVisible);
  document.removeEventListener("visibilitychange", handleBecameVisible);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", check);
  }
  timerIds.forEach(clearTimeout);
  timerIds = [];
  if (pollId) {
    clearInterval(pollId);
    pollId = 0;
  }
};
